import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { enablePush, pushAvailable, pushPermission } from "@/lib/push";

const DISMISS_KEY = "utile_push_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const SHOW_AFTER_MS = 20000;

const canAsk = () => pushAvailable() && pushPermission() === "default";
const snoozed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < SNOOZE_MS;

// Card used both as floating invite and inline after a booking (variant="inline")
export function PushInviteCard({ sessionId, onDone, variant = "floating" }) {
  const [busy, setBusy] = useState(false);
  const activate = async () => {
    setBusy(true);
    try {
      await enablePush(sessionId);
      toast.success("Notifiche attivate! Ti avviseremo su prenotazioni e nuove offerte.");
      onDone?.(true);
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
    } finally { setBusy(false); }
  };
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); onDone?.(false); };
  const inline = variant === "inline";
  return (
    <div data-testid={inline ? "push-invite-inline" : "push-invite"} className={`relative flex items-start gap-3 rounded-2xl p-4 text-left text-white ${inline ? "mb-6 border border-white/10 bg-white/[0.03]" : "bg-[#0e0e0e]/95 backdrop-blur-xl border border-[#22ff66]/40 shadow-2xl"}`}>
      <div className="shrink-0 w-10 h-10 rounded-full bg-[#22ff66]/15 flex items-center justify-center"><Bell className="w-5 h-5 text-[#22ff66]" /></div>
      <div className="min-w-0 flex-1 pr-5">
        <div className="text-sm font-bold">Attiva le notifiche</div>
        <div className="text-xs text-white/70 mt-1">Ti avvisiamo su prenotazioni e nuove offerte.</div>
        <button type="button" data-testid="push-enable-btn" onClick={activate} disabled={busy} className="mt-3 inline-flex items-center gap-2 bg-[#22ff66] text-black px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold active:scale-95 transition-transform disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Attiva
        </button>
      </div>
      <button type="button" data-testid="push-invite-close" onClick={dismiss} aria-label="Chiudi" className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PushInvite() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!canAsk() || snoozed()) return;
    let t;
    const tryShow = () => {
      if (document.querySelector('[data-testid="install-app-banner"]')) t = setTimeout(tryShow, SHOW_AFTER_MS);
      else setShow(true);
    };
    t = setTimeout(tryShow, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  if (!show || pathname.startsWith("/admin") || pathname.startsWith("/pagamento") || !canAsk()) return null;
  return (
    <div className="fixed inset-x-3 bottom-20 md:inset-x-auto md:left-6 md:bottom-6 md:w-[360px] z-[69] animate-in slide-in-from-bottom-6 fade-in duration-500">
      <PushInviteCard onDone={() => setShow(false)} />
    </div>
  );
}

export const shouldOfferPush = canAsk;
